import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { importAPI } from '../services/api';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import {
  Upload, Link2, ArrowRight, ArrowLeft, Download,
  CheckCircle, XCircle, AlertTriangle, Loader, FileSpreadsheet, RefreshCw, Layers, Wifi, Tv,
} from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';

// ── Template columns — must match backend routes/import.js TEMPLATE ───────────
// aliases let files with slightly different header names still work
const TEMPLATE = [
  { key: 'name',               header: 'name',                 required: true,  aliases: ['customername','fullname','customer'] },
  { key: 'mobile',             header: 'mobile',               required: true,  aliases: ['mobileno','phone','phoneno','contact','mobilenumber'] },
  { key: 'customerType',       header: 'customer_type',        required: true,  aliases: ['type','servicetype','connectiontype','service','category','custtype'] },
  { key: 'address',            header: 'address',              required: true,  aliases: ['addr','houseaddress','residence','fulladdress'] },
  { key: 'area',               header: 'area',                 required: true,  aliases: ['society','soceity','locality','societyname','zone','colony'] },
  { key: 'status',             header: 'status',               required: false, aliases: ['customerstatus','connectionstatus'] },
  { key: 'alternateMobile',    header: 'alternate_mobile',     required: false, aliases: ['altmobile','mobile2','secondarymobile'] },
  { key: 'email',              header: 'email',                required: false, aliases: ['emailid','mail'] },
  { key: 'city',               header: 'city',                 required: false, aliases: [] },
  { key: 'installationDate',   header: 'installation_date',    required: false, aliases: ['installdate','joindate','connectiondate','startdate'] },
  { key: 'houseNumber',        header: 'house_number',         required: false, aliases: ['houseno','flatno'] },
  { key: 'bbUsername',         header: 'PPPoE Username',       required: false, aliases: ['pppoe','username','pppoeusername','broadbanduser','bbusername'] },
  { key: 'routerModel',        header: 'router_model',         required: false, aliases: ['router','modem'] },
  { key: 'routerSerial',       header: 'router_serial',        required: false, aliases: ['routersn','modemserial'] },
  { key: 'ispName',            header: 'isp_name',             required: false, aliases: ['isp','provider','serviceprovider'] },
  { key: 'pon',                header: 'pon',                  required: false, aliases: ['ponnumber','ponport','ponno','oltport'] },
  { key: 'bbExpiry',           header: 'expiry',               required: false, aliases: ['expirydate','expdate','validity','validtill','expiryon'] },
  { key: 'staticIp',           header: 'static_ip',            required: false, aliases: ['staticip','ip'] },
  { key: 'stbSerial',          header: 'stb_serial',           required: false, aliases: ['stb','stbno','settoboxserial','stbnumber'] },
  { key: 'stbModel',           header: 'stb_model',            required: false, aliases: ['settoboxmodel','stbtype'] },
  { key: 'entityCode',         header: 'entity_code',          required: false, aliases: ['entity'] },
  { key: 'accountNumber',      header: 'account_number',       required: false, aliases: ['account','accountno','acno'] },
  { key: 'node',               header: 'node',                 required: false, aliases: ['distributionnode'] },
  { key: 'catvType',           header: 'catv_type',            required: false, aliases: ['catvtype','regulardtd'] },
  { key: 'packageName',        header: 'package_name',         required: false, aliases: ['package','packagename','pack','catvpackage'] },
  { key: 'securityDeposit',    header: 'security_deposit',     required: false, aliases: ['deposit','depositamount'] },
  { key: 'openingBalance',     header: 'opening_balance',      required: false, aliases: ['outstanding','balance','openingbal'] },
  { key: 'openingBalanceType', header: 'opening_balance_type', required: false, aliases: ['balancetype'] },
  { key: 'notes',              header: 'notes',                required: false, aliases: ['remarks','comment','description'] },
];

// Per-category templates — [fieldKey, csvHeader] pairs (must match backend routes/import.js).
// CableTV and Broadband each get their own file with their own column set.
// In the cable-TV file, customer_type means REGULAR | DTD (connection category) —
// the service type is implied by the upload category.
const CATV_COLUMNS = [
  ['name', 'name'], ['mobile', 'mobile'], ['address', 'address'], ['area', 'area'],
  ['status', 'status'], ['houseNumber', 'house_number'], ['stbSerial', 'stb_serial'],
  ['stbModel', 'stb_model'], ['entityCode', 'entity_code'], ['accountNumber', 'account_number'],
  ['node', 'node'], ['packageName', 'package_name'], ['securityDeposit', 'security_deposit'],
  ['openingBalance', 'outstanding_balance'], ['catvType', 'customer_type'],
];
const BB_COLUMNS = [
  ['area', 'Society'], ['bbUsername', 'PPPoE Username'], ['houseNumber', 'House No.'],
  ['name', 'Customer Name'], ['mobile', 'Mobile No.'], ['pon', 'PON'],
  ['routerModel', 'Router'], ['routerSerial', 'Router - S/N'], ['ispName', 'ISP'],
  ['address', 'Address'], ['bbExpiry', 'Expiry'],
];

const SAMPLES = {
  broadband: {
    name: 'Suresh Patel', mobile: '9876543210', customerType: 'broadband',
    address: '12 Rose Apartment, CG Road', area: 'Shyamal', status: 'active',
    alternateMobile: '9898011122', email: 'suresh@example.com', city: 'Ahmedabad',
    installationDate: '01/06/2025', houseNumber: 'A-12', bbUsername: 'suresh_pppoe',
    routerModel: 'TP-Link AX3000', routerSerial: 'SN4455667', ispName: 'GTPL',
    pon: 'PON-1/2/3', bbExpiry: '31/12/2026',
    securityDeposit: '500', openingBalance: '0', openingBalanceType: 'outstanding',
    notes: 'Fiber customer',
  },
  catv: {
    name: 'Meena Shah', mobile: '9825012345', customerType: 'catv',
    address: '3 Shanti Bungalow, Naranpura', area: 'Naranpura', status: 'active',
    city: 'Ahmedabad', installationDate: '15/03/2024', houseNumber: 'B-3',
    stbSerial: 'STB99887766', stbModel: 'DEN HD', entityCode: 'ENT001',
    accountNumber: 'AC123456', node: 'Node-4', catvType: 'REGULAR', packageName: 'Standard Pack',
    securityDeposit: '0', openingBalance: '250', openingBalanceType: 'outstanding',
    notes: 'Old cable customer',
  },
  both: {
    name: 'Rakesh Kumar', mobile: '9898054321', customerType: 'both',
    address: '45 Green Park Society', area: 'Vastrapur', status: 'active',
    email: 'rakesh@example.com', city: 'Ahmedabad', installationDate: '20/01/2026',
    houseNumber: 'C-45', bbUsername: 'rakesh_bb', routerModel: 'Netlink ONT',
    ispName: 'BSNL', stbSerial: 'STB11223344', entityCode: 'ENT002',
    accountNumber: 'AC654321', node: 'Node-2',
    securityDeposit: '1000', openingBalance: '0', openingBalanceType: 'outstanding',
  },
};

function templateColsFor(category) {
  if (category === 'catv')      return CATV_COLUMNS;
  if (category === 'broadband') return BB_COLUMNS;
  return TEMPLATE.map(f => [f.key, f.header]);
}

// Fields shown on the mapping-verification screen, labelled with the
// category template's own header names (e.g. catv shows "outstanding_balance").
function mappingFieldsFor(category) {
  const required = category === 'mixed'
    ? ['name', 'mobile', 'customerType', 'address', 'area']
    : ['name', 'mobile', 'address', 'area'];
  return templateColsFor(category).map(([key, header]) =>
    ({ key, header, required: required.includes(key) }));
}

const VALIDATE_CHUNK = 500;
const IMPORT_BATCH   = 200;

const norm = s => String(s).toLowerCase().replace(/[^a-z0-9]/g, '');

// Match file headers to template fields; unknown headers go to _extra (legacy data).
// overrides = { fieldKey: headerName } chosen manually by the user — they win over auto-matching.
// A '__skip__' override means "this field is not in the file" — blocks auto-matching for it.
function resolveHeaders(headers, overrides = {}) {
  const fieldByIdx = {};
  const claimedFields = new Set();
  const claimedIdx = new Set();

  Object.entries(overrides).forEach(([key, headerName]) => {
    if (headerName === '__skip__') { claimedFields.add(key); return; }
    const idx = headers.indexOf(headerName);
    if (idx >= 0) { fieldByIdx[idx] = key; claimedFields.add(key); claimedIdx.add(idx); }
  });

  // Pass 1: exact match on template name or alias
  headers.forEach((h, idx) => {
    if (claimedIdx.has(idx) || !h) return;
    const n = norm(h);
    const field = TEMPLATE.find(f => !claimedFields.has(f.key) &&
      (norm(f.header) === n || f.aliases.some(a => norm(a) === n)));
    if (field) { fieldByIdx[idx] = field.key; claimedFields.add(field.key); claimedIdx.add(idx); }
  });

  // Pass 2: fuzzy — header contains the field name or an alias (e.g. "FULL ADDRESS" → address)
  TEMPLATE.forEach(f => {
    if (claimedFields.has(f.key)) return;
    const needles = [norm(f.header), ...f.aliases.map(norm)].filter(x => x.length >= 4);
    const idx = headers.findIndex((h, i) =>
      h && !claimedIdx.has(i) && needles.some(nd => norm(h).includes(nd)));
    if (idx >= 0) { fieldByIdx[idx] = f.key; claimedFields.add(f.key); claimedIdx.add(idx); }
  });

  headers.forEach((h, idx) => { if (!(idx in fieldByIdx)) fieldByIdx[idx] = null; });
  return fieldByIdx;
}

function buildRecords(headers, rows, overrides = {}) {
  const fieldByIdx = resolveHeaders(headers, overrides);
  const unknownHeaders = headers.filter((h, idx) => h && !fieldByIdx[idx]);
  const missingRequired = TEMPLATE.filter(f => f.required &&
    !Object.values(fieldByIdx).includes(f.key));

  // Excel date cells arrive as JS Date objects (cellDates: true) — render as DD/MM/YYYY
  const cellText = c => c instanceof Date
    ? `${String(c.getDate()).padStart(2, '0')}/${String(c.getMonth() + 1).padStart(2, '0')}/${c.getFullYear()}`
    : String(c ?? '').trim();

  const records = rows.map((row, i) => {
    const rec = { _row: i + 2, _extra: {} };
    headers.forEach((h, idx) => {
      const val = cellText(row[idx]);
      if (fieldByIdx[idx]) rec[fieldByIdx[idx]] = val;
      else if (val) rec._extra[h || `column_${idx + 1}`] = val;
    });
    return rec;
  });
  return { records, unknownHeaders, missingRequired };
}

function parseFileInBrowser(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let wb;
        if (/\.csv$/i.test(file.name)) {
          let text = e.target.result;
          if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1); // strip BOM
          wb = XLSX.read(text, { type: 'string', cellDates: true });
        } else {
          wb = XLSX.read(e.target.result, { type: 'array', cellDates: true, raw: false });
        }
        const ws  = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        // Keep headers positional (empty cells included) so data columns don't shift
        const headers = (raw[0] || []).map(h => String(h).trim());
        const rows    = raw.slice(1).filter(r => r.some(c => String(c).trim() !== ''));
        if (headers.every(h => !h)) return reject(new Error('File appears empty — no column headers found'));
        resolve({ headers, rows });
      } catch (err) {
        reject(new Error(`Cannot parse file: ${err.message}`));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    if (/\.csv$/i.test(file.name)) reader.readAsText(file, 'utf-8');
    else reader.readAsArrayBuffer(file);
  });
}

function downloadTemplate(category = 'mixed') {
  const cols = templateColsFor(category);
  const samples = category === 'broadband' ? [SAMPLES.broadband]
    : category === 'catv' ? [SAMPLES.catv]
    : [SAMPLES.broadband, SAMPLES.catv, SAMPLES.both];
  const rows = samples.map(s => cols.map(([k]) => s[k] ?? ''));
  const lines = [cols.map(([, h]) => h), ...rows]
    .map(r => r.map(v => /[",\n]/.test(v) ? `"${String(v).replace(/"/g, '""')}"` : v).join(','));
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = category === 'catv' ? 'cabletv-import-template.csv'
    : category === 'broadband' ? 'broadband-import-template.csv'
    : 'customer-import-template.csv';
  a.click();
  URL.revokeObjectURL(a.href);
}

const STATUS_STYLE = {
  ready: { bg: 'var(--cb-d1fae5)', color: '#065f46', label: 'Ready' },
  merge: { bg: 'var(--cb-dbeafe)', color: '#1e3a8a', label: 'Will Merge' },
  skip:  { bg: 'var(--cb-fef3c7)', color: '#92400e', label: 'Duplicate' },
  error: { bg: 'var(--cb-fee2e2)', color: '#991b1b', label: 'Error' },
};

export default function ImportPage() {
  useDarkReady();
  const navigate = useNavigate();
  const fileRef  = useRef(null);

  const [step, setStep]         = useState(1); // 1=category+template+upload 2=review 3=results
  const [category, setCategory] = useState(''); // '' until picked — 'broadband' | 'catv' | 'mixed'
  const [source, setSource]     = useState('file'); // 'file' | 'sheet'
  const [sheetUrl, setSheetUrl] = useState('');
  const [loading, setLoading]   = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');

  const [records, setRecords]     = useState([]);   // canonical records incl. _row/_extra
  const [unknownCols, setUnknownCols] = useState([]);
  const [rowStatus, setRowStatus] = useState({});   // _row -> { status, message }
  const [errorsOnly, setErrorsOnly] = useState(false);

  // Manual column matching — used when required columns can't be auto-detected
  const [parsed, setParsed]         = useState(null); // { headers, rows } awaiting manual match
  const [matchFields, setMatchFields] = useState([]); // required template fields not auto-matched
  const [manualMap, setManualMap]   = useState({});   // field key -> chosen header name

  const [importing, setImporting] = useState(false);
  const [progress, setProgress]   = useState({ done: 0, total: 0 });
  const [results, setResults]     = useState(null);

  // ── Step 1: load + validate ────────────────────────────────────────────────

  const loadRows = async (headers, rows) => {
    if (rows.length === 0) { toast.error('No data rows found in the file'); return; }
    // Always stop at the mapping screen — the user verifies every column before
    // anything is validated or imported. Auto-detection only prefills the choices.
    const fieldByIdx = resolveHeaders(headers);
    const auto = {};
    Object.entries(fieldByIdx).forEach(([idx, key]) => { if (key) auto[key] = headers[idx]; });
    // Cable-TV files use their customer_type column for REGULAR | DTD → catvType
    if (category === 'catv' && auto.customerType && !auto.catvType) {
      auto.catvType = auto.customerType;
      delete auto.customerType;
    }
    setParsed({ headers, rows });
    setMatchFields(mappingFieldsFor(category));
    setManualMap(auto);
  };

  const validateAndGo = async (recs, unknownHeaders) => {
    // Flag in-file duplicates (first occurrence wins). Mobiles may repeat —
    // unique keys are account_number (CableTV) and username (broadband).
    const seenAcct = new Set();
    const seenUname = new Set();
    const localStatus = {};
    recs.forEach(r => {
      const acct = String(r.accountNumber || '').trim();
      if (acct && seenAcct.has(acct)) {
        localStatus[r._row] = { status: 'error', message: `Account number ${acct} appears more than once in this file` };
        return;
      }
      if (acct) seenAcct.add(acct);
      const uname = String(r.bbUsername || '').trim().toLowerCase();
      if (uname && seenUname.has(uname)) {
        localStatus[r._row] = { status: 'error', message: `Username ${r.bbUsername} appears more than once in this file` };
        return;
      }
      if (uname) seenUname.add(uname);
    });

    // Server-side dry run in chunks
    setLoadingMsg(`Checking ${recs.length} rows…`);
    const toValidate = recs.filter(r => !localStatus[r._row]);
    try {
      for (let i = 0; i < toValidate.length; i += VALIDATE_CHUNK) {
        const chunk = toValidate.slice(i, i + VALIDATE_CHUNK);
        const { data } = await importAPI.validate({ records: chunk });
        data.data.rows.forEach(r => { localStatus[r.row] = { status: r.status, message: r.message }; });
        setLoadingMsg(`Checking ${Math.min(i + VALIDATE_CHUNK, toValidate.length)} / ${recs.length} rows…`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Validation failed');
      return;
    }

    setRecords(recs);
    setUnknownCols(unknownHeaders);
    setRowStatus(localStatus);
    setStep(2);
  };

  const confirmMatch = async () => {
    const unpicked = matchFields.filter(f => f.required && !manualMap[f.key]);
    if (unpicked.length > 0) {
      toast.error(`Select a column for: ${unpicked.map(f => f.header).join(', ')}`);
      return;
    }
    // One file column must not feed two fields
    const picks = Object.values(manualMap).filter(v => v && !v.startsWith('__'));
    const dup = picks.find((v, i) => picks.indexOf(v) !== i);
    if (dup) { toast.error(`Column "${dup}" is mapped to more than one field`); return; }

    setLoading(true); setLoadingMsg('Checking rows…');
    try {
      // "__all__:<value>" means apply a constant to every row instead of reading a column
      const consts = {}, colMap = {};
      Object.entries(manualMap).forEach(([k, v]) => {
        if (v.startsWith('__all__:')) consts[k] = v.slice(8); else if (v) colMap[k] = v;
      });
      // Fields the user explicitly left as "not in this file" must not be auto-matched
      matchFields.forEach(f => { if (!manualMap[f.key]) colMap[f.key] = '__skip__'; });
      const { records: recs, unknownHeaders } = buildRecords(parsed.headers, parsed.rows, colMap);
      recs.forEach(r => Object.assign(r, consts));
      if (category !== 'mixed') recs.forEach(r => { if (!String(r.customerType || '').trim()) r.customerType = category; });
      await validateAndGo(recs, unknownHeaders);
    } finally {
      setLoading(false); setLoadingMsg('');
    }
  };

  const loadFile = async (file) => {
    setLoading(true); setLoadingMsg('Parsing file…');
    try {
      const { headers, rows } = await parseFileInBrowser(file);
      await loadRows(headers, rows);
    } catch (err) {
      toast.error(err.message || 'Could not parse file');
    } finally {
      setLoading(false); setLoadingMsg('');
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const loadSheet = async () => {
    if (!sheetUrl.trim()) { toast.error('Paste a Google Sheets URL'); return; }
    setLoading(true); setLoadingMsg('Fetching sheet…');
    try {
      const { data } = await importAPI.previewSheet(sheetUrl.trim());
      if (!data.success) throw new Error(data.message);
      await loadRows(data.data.headers, data.data.rows);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not fetch sheet');
    } finally {
      setLoading(false); setLoadingMsg('');
    }
  };

  // ── Step 2 → 3: import ─────────────────────────────────────────────────────

  const counts = useMemo(() => {
    const c = { ready: 0, merge: 0, skip: 0, error: 0 };
    records.forEach(r => { const s = rowStatus[r._row]?.status; if (c[s] !== undefined) c[s]++; });
    return c;
  }, [records, rowStatus]);

  const importable = useMemo(
    () => records.filter(r => ['ready', 'merge'].includes(rowStatus[r._row]?.status)),
    [records, rowStatus]
  );

  const runImport = async () => {
    if (importable.length === 0) { toast.error('No importable rows'); return; }
    setImporting(true);
    setProgress({ done: 0, total: importable.length });
    const combined = { success: 0, merged: 0, skipped: counts.skip, failed: 0, errors: [] };

    // Rows already known to be errors/duplicates are reported without sending them
    records.forEach(r => {
      const st = rowStatus[r._row];
      if (st?.status === 'error') { combined.failed++; combined.errors.push({ row: r._row, error: st.message }); }
      if (st?.status === 'skip')  { combined.errors.push({ row: r._row, error: st.message }); }
    });

    try {
      for (let b = 0; b < importable.length; b += IMPORT_BATCH) {
        const batch = importable.slice(b, b + IMPORT_BATCH);
        const { data } = await importAPI.run({ records: batch });
        combined.success += data.data.success;
        combined.merged  += data.data.merged  || 0;
        combined.skipped += data.data.skipped || 0;
        combined.failed  += data.data.failed;
        combined.errors.push(...(data.data.errors || []));
        setProgress({ done: Math.min(b + IMPORT_BATCH, importable.length), total: importable.length });
      }
      setResults(combined);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setStep(1); setRecords([]); setRowStatus({}); setUnknownCols([]);
    setResults(null); setSheetUrl(''); setErrorsOnly(false);
    setParsed(null); setMatchFields([]); setManualMap({});
  };

  const visibleRows = errorsOnly
    ? records.filter(r => ['error', 'skip'].includes(rowStatus[r._row]?.status))
    : records;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/customers')}>
          <ArrowLeft size={14} /> Back
        </button>
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>Import Customers</h2>
          <p className="text-muted text-small" style={{ margin: 0 }}>Choose category, fill the CSV template, upload, review, import</p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => downloadTemplate(category || 'mixed')} style={{ marginLeft: 'auto' }}>
          <Download size={13} /> CSV Template
        </button>
      </div>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 28, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)' }}>
        {[
          { n: 1, label: 'Template & Upload' },
          { n: 2, label: 'Review & Validate' },
          { n: 3, label: 'Results' },
        ].map(s => (
          <div key={s.n} style={{
            flex: 1, padding: '10px 16px', textAlign: 'center',
            background: step === s.n ? 'var(--primary)' : step > s.n ? 'var(--cb-d1fae5)' : 'var(--card-bg)',
            color: step === s.n ? '#fff' : step > s.n ? '#065f46' : 'var(--text-muted)',
            fontWeight: step === s.n ? 700 : 500, fontSize: '0.85rem',
            borderRight: s.n < 3 ? '1px solid var(--border)' : 'none',
          }}>
            {step > s.n && <CheckCircle size={13} style={{ marginRight: 5, verticalAlign: 'middle' }} />}
            Step {s.n}: {s.label}
          </div>
        ))}
      </div>

      {/* ── STEP 1b: Verify column mapping (always shown before validation) ── */}
      {step === 1 && parsed && (
        <div className="card">
          <div className="card-header">
            <h3 style={{ margin: 0 }}>
              <CheckCircle size={15} style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--primary)' }} />
              Verify Column Mapping
            </h3>
          </div>
          <div className="card-body">
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 0 }}>
              Your sheet has <strong>{parsed.headers.filter(Boolean).length} columns</strong> and{' '}
              <strong>{parsed.rows.length} rows</strong>. Check that every field reads from the right
              column — auto-detected matches are prefilled. Nothing is imported until you continue.
            </p>
            <table style={{ borderCollapse: 'collapse', marginBottom: 16 }}>
              <tbody>
                {matchFields.map(f => (
                  <tr key={f.key}>
                    <td style={{ padding: '6px 16px 6px 0', fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {f.header}{f.required && <span className="required"> *</span>}
                    </td>
                    <td style={{ padding: '6px 0' }}>
                      <select
                        className="form-control"
                        style={{
                          fontSize: '0.83rem', minWidth: 280,
                          borderColor: f.required && !manualMap[f.key] ? 'var(--cb-dc2626)' : undefined,
                        }}
                        value={manualMap[f.key] || ''}
                        onChange={e => setManualMap(m => ({ ...m, [f.key]: e.target.value }))}
                      >
                        <option value="">— not in this file —</option>
                        {f.key === 'customerType' && (
                          <>
                            <option value="__all__:broadband">All rows are Broadband</option>
                            <option value="__all__:catv">All rows are CableTV</option>
                            <option value="__all__:both">All rows are Combo (both)</option>
                          </>
                        )}
                        {parsed.headers.map((h, idx) => {
                          if (!h) return null;
                          const sample = String(parsed.rows[0]?.[idx] ?? '').trim();
                          return (
                            <option key={idx} value={h}>
                              {h}{sample ? ` — e.g. "${sample.slice(0, 30)}"` : ''}
                            </option>
                          );
                        })}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(() => {
              const chosen = new Set(Object.values(manualMap).filter(v => v && !v.startsWith('__')));
              const unmapped = parsed.headers.filter(h => h && !chosen.has(h));
              return unmapped.length > 0 && (
                <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  Not mapped (kept as legacy data on each customer):{' '}
                  {unmapped.map(h => <code key={h} style={{ marginRight: 6 }}>{h}</code>)}
                </p>
              );
            })()}
            {category === 'mixed' && (
              <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                Tip: <code>customer_type</code> should contain <strong>broadband</strong>, <strong>catv</strong> or{' '}
                <strong>both</strong> per row. If your sheet doesn't have such a column, add one in Google Sheets
                and fill it for all rows, then reload the link.
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn btn-secondary" onClick={reset}>Back</button>
              <button className="btn btn-primary" onClick={confirmMatch} disabled={loading} style={{ minWidth: 160 }}>
                {loading
                  ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> {loadingMsg || 'Checking…'}</>
                  : <>Continue <ArrowRight size={14} /></>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── STEP 1: Category, Template & Upload ── */}
      {step === 1 && !parsed && (
        <div>
          {/* Category — picked first so the file doesn't need a customer_type column */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body">
              <div style={{ fontWeight: 700, marginBottom: 10 }}>1. What are you importing?</div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {[
                  { key: 'broadband', label: 'Broadband', icon: Wifi, color: 'var(--accent)',
                    desc: 'Every row imported as broadband — no customer_type column needed' },
                  { key: 'catv', label: 'CableTV', icon: Tv, color: 'var(--primary)',
                    desc: 'Every row imported as CableTV — no customer_type column needed' },
                  { key: 'mixed', label: 'Mixed File', icon: Layers, color: '#64748b',
                    desc: 'File has a customer_type column: broadband, catv or both per row' },
                ].map(opt => {
                  const active = category === opt.key;
                  return (
                    <button key={opt.key} type="button" onClick={() => setCategory(opt.key)}
                      style={{
                        flex: '1 1 200px', padding: '14px 16px', borderRadius: 12, cursor: 'pointer',
                        border: `2.5px solid ${active ? opt.color : '#e5e7eb'}`,
                        background: active ? `${opt.color}14` : 'var(--card-bg)',
                        textAlign: 'left',
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <opt.icon size={18} color={active ? opt.color : '#9ca3af'} />
                        <span style={{ fontWeight: 700, color: active ? opt.color : 'var(--text)' }}>{opt.label}</span>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {category && (
          <>
          {/* Template explainer */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <FileSpreadsheet size={34} color="var(--primary)" />
              <div style={{ flex: 1, minWidth: 260 }}>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>2. Download the CSV template</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  {category === 'mixed' ? (
                    <>Fill one row per customer. Required columns: <strong>name, mobile, customer_type, address, area</strong>.
                    Set <code>customer_type</code> to <strong>broadband</strong>, <strong>catv</strong> or <strong>both</strong> per row —
                    you can mix all types in one file. Extra columns are kept as legacy history.</>
                  ) : category === 'catv' ? (
                    <>Fill one row per customer. Required columns: <strong>name, mobile, address, area</strong>.
                    Every row imports as <strong>CableTV</strong> — set <code>customer_type</code> to{' '}
                    <strong>REGULAR</strong> or <strong>DTD</strong> per row (or leave blank).{' '}
                    <code>package_name</code> links the CATV package — unknown packages are created automatically.
                    Extra columns are kept as legacy history.</>
                  ) : (
                    <>Fill one row per customer. Required columns: <strong>Customer Name, Mobile No., Society, Address</strong>.
                    Every row will be imported as <strong>Broadband</strong> —
                    no customer_type column needed. Extra columns are kept as legacy history.</>
                  )}
                </div>
              </div>
              <button className="btn btn-primary" onClick={() => downloadTemplate(category)}>
                <Download size={14} /> Download Template
              </button>
            </div>
          </div>

          {/* Source toggle */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            {['file', 'sheet'].map(s => (
              <button key={s} type="button" onClick={() => setSource(s)}
                style={{
                  flex: 1, padding: '14px 20px', borderRadius: 12, cursor: 'pointer',
                  border: `2.5px solid ${source === s ? 'var(--primary)' : 'var(--cb-e5e7eb)'}`,
                  background: source === s ? 'var(--cb-eff6ff)' : 'var(--card-bg)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                }}>
                {s === 'file' ? <FileSpreadsheet size={20} color={source === s ? 'var(--primary)' : '#9ca3af'} /> : <Link2 size={20} color={source === s ? 'var(--primary)' : '#9ca3af'} />}
                <span style={{ fontWeight: 600, color: source === s ? 'var(--primary)' : 'var(--text)' }}>
                  {s === 'file' ? '3. Upload Filled File' : '3. Or Google Sheets URL'}
                </span>
              </button>
            ))}
          </div>

          {source === 'file' && (
            <div className="card">
              <div className="card-body" style={{ textAlign: 'center', padding: '36px 24px' }}>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }}
                  onChange={e => e.target.files[0] && loadFile(e.target.files[0])} />
                <div
                  onClick={() => !loading && fileRef.current?.click()}
                  style={{ border: '2px dashed var(--cb-cbd5e1)', borderRadius: 12, padding: '32px 24px', cursor: 'pointer' }}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--primary)'; }}
                  onDragLeave={e => { e.currentTarget.style.borderColor = 'var(--cb-cbd5e1)'; }}
                  onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--cb-cbd5e1)'; const f = e.dataTransfer.files[0]; if (f && !loading) loadFile(f); }}
                >
                  {loading
                    ? <><Loader size={32} color="var(--primary)" style={{ margin: '0 auto 12px', display: 'block', animation: 'spin 1s linear infinite' }} /><p>{loadingMsg}</p></>
                    : <><Upload size={32} color="#9ca3af" style={{ margin: '0 auto 12px', display: 'block' }} />
                        <p style={{ fontWeight: 600, marginBottom: 4 }}>Click to browse or drag & drop</p>
                        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>CSV, XLSX or XLS — parsed locally, then checked against the database</p>
                      </>
                  }
                </div>
              </div>
            </div>
          )}

          {source === 'sheet' && (
            <div className="card">
              <div className="card-body">
                <div style={{ background: 'var(--cb-fefce8)', border: '1px solid var(--cb-fde68a)', borderRadius: 8, padding: '12px 14px', marginBottom: 16, fontSize: '0.83rem', color: 'var(--c-78350f)' }}>
                  <strong>Before pasting:</strong> In Google Sheets → <em>Share</em> → set to <strong>"Anyone with the link — Viewer"</strong>.
                  The sheet must use the template column names.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="form-control" value={sheetUrl} onChange={e => setSheetUrl(e.target.value)}
                    placeholder="https://docs.google.com/spreadsheets/d/..." style={{ flex: 1 }} />
                  <button className="btn btn-primary" onClick={loadSheet} disabled={loading}>
                    {loading ? <><Loader size={14} /> {loadingMsg || 'Loading…'}</> : <><ArrowRight size={14} /> Load</>}
                  </button>
                </div>
              </div>
            </div>
          )}
          </>
          )}
        </div>
      )}

      {/* ── STEP 2: Review & Validate ── */}
      {step === 2 && (
        <div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
            <button className="btn btn-secondary btn-sm" onClick={reset}><ArrowLeft size={13} /> Start Over</button>
            <div><strong>{records.length}</strong> rows checked against the database — nothing imported yet.</div>
          </div>

          {/* Summary tiles */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
            {[
              { k: 'ready', icon: <CheckCircle size={20} color="#10b981" />, border: 'var(--cb-10b981)' },
              { k: 'merge', icon: <Layers size={20} color="var(--primary)" />,      border: 'var(--primary)' },
              { k: 'skip',  icon: <AlertTriangle size={20} color="#f59e0b" />, border: 'var(--cb-f59e0b)' },
              { k: 'error', icon: <XCircle size={20} color="#ef4444" />,     border: 'var(--cb-ef4444)' },
            ].map(({ k, icon, border }) => (
              <div key={k} className="card" style={{ textAlign: 'center', borderTop: `3px solid ${counts[k] > 0 ? border : 'var(--cb-e5e7eb)'}` }}>
                <div className="card-body" style={{ padding: '12px 8px' }}>
                  {icon}
                  <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{counts[k]}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {k === 'ready' ? 'New — will create' : k === 'merge' ? 'Will merge to Combo' : k === 'skip' ? 'Duplicates — skipped' : 'Errors — not imported'}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {unknownCols.length > 0 && (
            <div style={{ background: 'var(--cb-fffbeb)', border: '1px solid var(--cb-fde68a)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: '0.8rem', color: 'var(--c-78350f)' }}>
              <strong>{unknownCols.length} extra columns</strong> not in the template will be stored as legacy history: {unknownCols.join(', ')}
            </div>
          )}

          {/* Row detail table */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Row Details</h3>
              {(counts.error > 0 || counts.skip > 0) && (
                <label style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={errorsOnly} onChange={e => setErrorsOnly(e.target.checked)} />
                  Show only problems ({counts.error + counts.skip})
                </label>
              )}
            </div>
            <div className="card-body" style={{ padding: 0, maxHeight: 380, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ background: 'var(--bg)', position: 'sticky', top: 0 }}>
                    <th style={{ padding: '7px 12px', textAlign: 'left', width: 55 }}>Row</th>
                    <th style={{ padding: '7px 12px', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '7px 12px', textAlign: 'left' }}>Mobile</th>
                    {category === 'mixed' && <th style={{ padding: '7px 12px', textAlign: 'left' }}>Type</th>}
                    <th style={{ padding: '7px 12px', textAlign: 'left' }}>Area</th>
                    <th style={{ padding: '7px 12px', textAlign: 'left', width: 100 }}>Status</th>
                    <th style={{ padding: '7px 12px', textAlign: 'left' }}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.slice(0, 300).map(r => {
                    const st = rowStatus[r._row] || { status: 'error', message: 'Not validated' };
                    const style = STATUS_STYLE[st.status] || STATUS_STYLE.error;
                    return (
                      <tr key={r._row} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '5px 12px', color: 'var(--text-muted)' }}>{r._row}</td>
                        <td style={{ padding: '5px 12px', fontWeight: 600 }}>{r.name || '—'}</td>
                        <td style={{ padding: '5px 12px' }}>{r.mobile || '—'}</td>
                        {category === 'mixed' && <td style={{ padding: '5px 12px' }}>{r.customerType || '—'}</td>}
                        <td style={{ padding: '5px 12px' }}>{r.area || '—'}</td>
                        <td style={{ padding: '5px 12px' }}>
                          <span style={{ background: style.bg, color: style.color, padding: '2px 8px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                            {style.label}
                          </span>
                        </td>
                        <td style={{ padding: '5px 12px', color: 'var(--text-muted)' }}>{st.message}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {visibleRows.length > 300 && (
                <div style={{ padding: '8px 14px', fontSize: '0.78rem', color: 'var(--text-muted)', borderTop: '1px solid var(--border)' }}>
                  Showing first 300 of {visibleRows.length} rows. All {importable.length} valid rows will still be imported.
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="btn btn-secondary" onClick={reset}>Cancel</button>
            <button className="btn btn-primary" onClick={runImport} disabled={importing || importable.length === 0} style={{ minWidth: 220 }}>
              {importing
                ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Importing… {progress.done}/{progress.total}</>
                : <><Upload size={14} /> Import {importable.length} Customers</>
              }
            </button>
          </div>

          {importing && progress.total > 0 && (
            <div style={{ marginTop: 12, background: 'var(--card-bg)', borderRadius: 8, padding: '10px 14px', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: 6, color: 'var(--text-muted)' }}>
                <span>Importing in batches of {IMPORT_BATCH}…</span>
                <span>{Math.round((progress.done / progress.total) * 100)}%</span>
              </div>
              <div style={{ background: 'var(--border)', borderRadius: 4, height: 6 }}>
                <div style={{
                  background: 'var(--primary)', borderRadius: 4, height: 6,
                  width: `${(progress.done / progress.total) * 100}%`, transition: 'width 0.3s ease',
                }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── STEP 3: Results ── */}
      {step === 3 && results && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
            <div className="card" style={{ flex: 1, textAlign: 'center', borderTop: '3px solid var(--cb-10b981)' }}>
              <div className="card-body">
                <CheckCircle size={28} color="#10b981" style={{ margin: '0 auto 8px', display: 'block' }} />
                <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--c-10b981)' }}>{results.success}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Created</div>
              </div>
            </div>
            {results.merged > 0 && (
              <div className="card" style={{ flex: 1, textAlign: 'center', borderTop: '3px solid var(--primary)' }}>
                <div className="card-body">
                  <Layers size={28} color="var(--primary)" style={{ margin: '0 auto 8px', display: 'block' }} />
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--primary)' }}>{results.merged}</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Upgraded to Combo</div>
                </div>
              </div>
            )}
            {results.skipped > 0 && (
              <div className="card" style={{ flex: 1, textAlign: 'center', borderTop: '3px solid var(--cb-f59e0b)' }}>
                <div className="card-body">
                  <AlertTriangle size={28} color="#f59e0b" style={{ margin: '0 auto 8px', display: 'block' }} />
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--c-f59e0b)' }}>{results.skipped}</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Skipped (duplicate)</div>
                </div>
              </div>
            )}
            <div className="card" style={{ flex: 1, textAlign: 'center', borderTop: `3px solid ${results.failed > 0 ? 'var(--cb-ef4444)' : 'var(--cb-e5e7eb)'}` }}>
              <div className="card-body">
                <XCircle size={28} color={results.failed > 0 ? '#ef4444' : '#9ca3af'} style={{ margin: '0 auto 8px', display: 'block' }} />
                <div style={{ fontSize: '2rem', fontWeight: 700, color: results.failed > 0 ? 'var(--c-ef4444)' : 'var(--text-muted)' }}>{results.failed}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Failed</div>
              </div>
            </div>
          </div>

          {results.errors.length > 0 && (
            <div className="card mb-4">
              <div className="card-header" style={{ background: 'var(--cb-fef2f2)', borderBottom: '1px solid var(--cb-fecaca)' }}>
                <h3 style={{ color: 'var(--c-dc2626)', margin: 0 }}>Skipped / Errors ({results.errors.length})</h3>
              </div>
              <div className="card-body" style={{ padding: 0, maxHeight: 320, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg)' }}>
                      <th style={{ padding: '6px 14px', textAlign: 'left', width: 70 }}>Row</th>
                      <th style={{ padding: '6px 14px', textAlign: 'left' }}>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.errors.map((e, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '5px 14px', color: 'var(--c-ef4444)', fontWeight: 600 }}>Row {e.row}</td>
                        <td style={{ padding: '5px 14px' }}>{e.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={reset}>
              <RefreshCw size={14} /> Import Another File
            </button>
            <button className="btn btn-primary" onClick={() => navigate('/customers')}>
              View Customers →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
